import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import csvImgSrc from '../../../../../../../../../../src/assets/images/attachmentExcel16.svg';
import FrontImgSrc from '../../../../../../../../../../src/assets/images/frontLogo16.svg';
import {MappingHeader} from '../../../../../../../../../../src/components/common/mapping/mappingHeader';
import {MappingRowField} from '../../../../../../../../../../src/components/common/mapping/mappingRowField';
import {AccountsCsvStandardFieldsEnum} from '../../../../../../../../../../src/store/panels/contacts/contactsManager/file/contactsManagerFileState';
import {
  Handler,
  HandlerOf,
  RendererOf,
} from '../../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../../../../../shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../../../../shared/ui/src/components/tooltips/tooltipCoordinator';
import {UserThemeConsumer} from '../../../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {MappingRow} from '../../../../common/mapping/mappingRow';
import {ErrorWarningBox, ErrorWarningBoxTypesEnum} from '../accountsCsvImportWarningErrorBox/errorWarningBox';
import {CustomFieldDropdownContainer} from './customFieldDropdownContainer';

/*
 * Constants.
 */

const numLoadingRows = 6;
const tooltipWidth = 240; // px

/*
 * Props.
 */

export interface AccountsCsvImportMappingProps {
  isLoading: boolean;
  isDisabled: boolean;
  isShowingAdminError: boolean;
  standardHeaders: string[];
  customHeaders: string[];
  mapping: Record<string, number>;
  onWarningIconClick: Handler;
  onNavigateToLink: Handler;
  onMappingChange: HandlerOf<Record<string, number>>;
  renderWarningMessage: RendererOf<string>;
}

/*
 * Styles.
 */

const StyledMappingWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 40px 1fr 28px;
`;

const StyledContentDiv = styled.div`
  width: 600px;
`;

const StyledCustomFieldDiv = styled.div`
  width: fit-content;
  padding: 0px 12px;
`;

const StyledStandardFieldDiv = styled.div`
  width: fit-content;
  padding: 0px 6px;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  border-radius: 8px;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${(p) => p.theme.greys.shade10};
  }
`;

const StyledIconWrapperDiv = styled.div`
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledCsvHeaderFieldDiv = styled.div`
  padding: 4px 12px;
  display: flex;
  justify-content: start;
  align-items: center;
`;

const StyledErrorBoxWrapperDiv = styled.div`
  max-width: 600px;
  margin-bottom: 16px;
`;

/*
 * Component.
 */

export const AccountsCsvImportMapping: FC<AccountsCsvImportMappingProps> = ({
  isLoading,
  isDisabled,
  isShowingAdminError,
  standardHeaders,
  customHeaders,
  mapping,
  onWarningIconClick,
  onNavigateToLink,
  onMappingChange,
  renderWarningMessage,
}) => {
  if (isDisabled) {
    return null;
  }

  const onSelect = (newMapping: Record<string, number>) => {
    onMappingChange({...mapping, ...newMapping});
  };

  const renderAccountCustomField = (header: string) => (
    <StyledCustomFieldDiv>
      <CustomFieldDropdownContainer
        selectedCustomFieldId={mapping[header]}
        onSelect={(customFieldId) => onSelect({[header]: customFieldId})}
        allSelectedCustomFieldIds={Object.values(mapping)}
        defaultCustomFieldName={header}
        onNavigateToLink={onNavigateToLink}
        isDisabled={isShowingAdminError}
      />
    </StyledCustomFieldDiv>
  );

  const renderAccountStandardField = (header: string) => (
    <StyledStandardFieldDiv>
      <MappingRowField isReadOnly iconName={computeIconName(header)}>
        {computeStandardFieldName(header)}
      </MappingRowField>
    </StyledStandardFieldDiv>
  );

  return (
    <>
      {isShowingAdminError && (
        <StyledErrorBoxWrapperDiv>
          <ErrorWarningBox
            type={ErrorWarningBoxTypesEnum.ERROR}
            title={
              <Trans id="accounts-csv-import-mapping-admin-error-box-title">
                Administrator privileges required
              </Trans>
            }
            descriptionText={
              <Trans id="accounts-csv-import-mapping-admin-error-box-description">
                Only admins can create custom fields. Please ask an admin to create the custom fields you need
                so you can complete this step.
              </Trans>
            }
          />
        </StyledErrorBoxWrapperDiv>
      )}
      <StyledContentDiv>
        <StyledMappingWrapperDiv>
          <MappingHeader
            isLoading={isLoading}
            sourceImg={csvImgSrc}
            sourceTitle={<Trans id="accounts-csv-import-csv-headers">CSV column headers</Trans>}
            destinationImg={FrontImgSrc}
            destinationTitle={<Trans id="accounts-csv-import-front-fields">Front account fields</Trans>}
          />
          {renderLoadingRows(isLoading)}
          {renderMappingRows(
            standardHeaders,
            isLoading,
            renderAccountStandardField,
            renderWarningMessage,
            onWarningIconClick,
          )}
          {renderMappingRows(
            customHeaders,
            isLoading,
            renderAccountCustomField,
            renderWarningMessage,
            onWarningIconClick,
          )}
        </StyledMappingWrapperDiv>
      </StyledContentDiv>
    </>
  );
};

/*
 * Helpers.
 */

function renderLoadingRows(isLoading: boolean) {
  if (!isLoading) {
    return null;
  }
  const numArray = [...Array(numLoadingRows).keys()];
  return numArray.map((i) => <MappingRow key={i} isLoading opacity={1 - (1 / numLoadingRows) * i} />);
}

function renderMappingRows(
  headers: string[],
  isLoading: boolean,
  renderDestinationField: RendererOf<string>,
  renderWarningMessage: RendererOf<string>,
  onWarningIconClick: Handler,
) {
  if (isLoading) {
    return null;
  }

  return headers.map((header) => (
    <MappingRow
      key={header}
      isLoading={isLoading}
      renderSourceField={() => renderCsvHeaderField(header)}
      renderDestinationField={() => renderDestinationField(header)}
      renderItem={() => renderWarningIcon(onWarningIconClick, renderWarningMessage(header))}
    />
  ));
}

function renderCsvHeaderField(header: string) {
  return <StyledCsvHeaderFieldDiv>{header}</StyledCsvHeaderFieldDiv>;
}

function computeIconName(header: string): IconName | undefined {
  const normalizedHeader = header.toLowerCase();
  switch (normalizedHeader) {
    case AccountsCsvStandardFieldsEnum.domains:
      return 'mentionSmall';
    case AccountsCsvStandardFieldsEnum.name:
    case AccountsCsvStandardFieldsEnum.description:
      return 'contentSmall';
    case AccountsCsvStandardFieldsEnum.externalid:
      return 'numbersSmall';
    default:
      return undefined;
  }
}

function computeStandardFieldName(header: string) {
  const normalizedHeader = header.toLowerCase();
  switch (normalizedHeader) {
    case AccountsCsvStandardFieldsEnum.domains:
      return <Trans id="account-csv-import-standard-field-email">Email</Trans>;
    case AccountsCsvStandardFieldsEnum.name:
      return <Trans id="account-csv-import-standard-field-name">Name</Trans>;
    case AccountsCsvStandardFieldsEnum.description:
      return <Trans id="account-csv-import-standard-field-description">Description</Trans>;
    case AccountsCsvStandardFieldsEnum.externalid:
      return <Trans id="account-csv-import-standard-field-external-id">External ID</Trans>;
    default:
      return null;
  }
}

function renderWarningIcon(onWarningIconClick: Handler, warningMessage?: ReactNode) {
  if (!warningMessage) {
    return <div />;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconWrapperDiv onClick={onWarningIconClick}>
          <StyledTooltipCoordinator
            isMultiline
            maxWidth={tooltipWidth}
            render={() => warningMessage}
            placement="top"
          >
            <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} hasHover />
          </StyledTooltipCoordinator>
        </StyledIconWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
